
/**
 * Module dependencies.
 */

import { CollectionProps, MediaProps } from 'src/types/collection';
import { PlayVideoButton } from 'src/components/core/video-player';
import { color, media, units } from 'src/styles/utils';
import { ifNotProp, ifProp, theme } from 'styled-tools';
import { kioskRoutes } from 'src/core/routes';
import { useTranslate } from 'src/core/utils/translator';
import Container from 'src/components/core/layout/container';
import FadeInUp from 'src/components/core/animations/fade-in-up';
import GalleryNavigation from 'src/components/core/slider/gallery-navigation';
import Image from 'src/components/core/image';
import Label from 'src/components/core/typography/label';
import Lightbox from 'src/components/lightbox';
import Navigation from 'src/components/navigation';
import ParallaxFadeInUp from 'src/components/core/animations/parallax-fade-in-up';
import React, {
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';

import Slider, { Slide } from 'src/components/core/slider';
import Type from 'src/components/core/typography';
import Typology from './typology';
import camelCase from 'lodash/camelCase';
import map from 'lodash/map';
import size from 'lodash/size';
import styled, { css } from 'styled-components';

/**
 * `GalleryItem` props.
 */

type GalleryItem = {
  coverImage?: string | null,
  label?: string | null,
  type: 'image' | 'video',
  url: string
};

/**
 * `Props` type.
 */

type Props = Pick<CollectionProps, | 'name' | 'typologies' | 'subTitle'> & {
  colorsTotal: number,
  finishesTotal: number,
  formatsTotal: number,
  gallery: GalleryItem[],
  imageUrl: string | null,
  isKiosk?: boolean,
  isNew: boolean,
  media: MediaProps[],
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.header<{ isKiosk?: boolean }>`
  padding-top: ${theme('dimensions.navbarHeightMobile')}px;
  position: relative;

  ${media.min('ms')`
    padding-top: ${theme('dimensions.navbarHeight')}px;
  `}

  ${ifNotProp('isKiosk', css`
    min-height: 100vh;
  `)}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div<{ isKiosk?: boolean }>`
  display: grid;
  grid-template-areas:
    'isNew      isNew      .             '
    'title      title      title         '
    '.          gallery    gallery       '
    'image      image      galleryDetails'
    'typologies typologies typologies    '
    'specs      specs      specs         ';
  grid-template-columns: ${units(2.5)} calc(50% - ${units(2.5)}) 50%;
  grid-template-rows: repeat(2, max-content) minmax(225px, 1fr) ${units(4)} repeat(3, max-content);
  padding-top: ${units(6)};

  ${media.min('ms')`
    grid-template-areas:
      '. isNew isNew   .              .'
      '. title title   title          .'
      '. .     gallery gallery        .'
      '. image image   galleryDetails .'
      '. image image   typologies     .'
      '. specs specs   typologies     .';
    grid-template-columns: 1fr 2fr 2fr 6fr 1fr;
    grid-template-rows: repeat(2, max-content) 2fr 2fr repeat(2, 1fr) 2fr max-content;

    ${ifProp('isKiosk', css`
      grid-template-areas:
        '. back  back    back           .'
        '. isNew isNew   .              .'
        '. title title   title          .'
        '. .     gallery gallery        .'
        '. image image   galleryDetails .'
        '. image image   typologies     .'
        '. specs specs   typologies     .';
      grid-template-rows: repeat(3, max-content) 2fr 2fr 1fr 0.75fr 2fr max-content;
    `)}
  `}

  ${media.min('lg')`
    grid-template-rows: repeat(2, max-content) 4fr 2fr repeat(2, 1fr) 2fr minmax(120px, max-content);
  `}
`;

/**
 * `IsNew` styled component.
 */

const IsNew = styled(Label).attrs({ size: 'small' })`
  color: ${color('brown400')};
  font-style: italic;
  grid-area: isNew;
  grid-row: 1;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H1)<{ isKiosk?: boolean }>`
  grid-area: title;
  grid-row: 2;
  margin-bottom: ${units(3.5)};
  position: relative;
  z-index: 1;

  ${media.min('ms')`
    margin-bottom: ${ifProp('isKiosk', units(4.5), units(-6))};
  `}
`;

/**
 * `SubTitle` styled component.
 */

const SubTitle = styled(Type.H6).attrs({
  as: 'span'
})`
  ${media.min('ms')`
    bottom: -${units(1.5)};
    left: 0;
    padding-right: ${units(1)};
    position: absolute;
    transform: translateY(100%);
    width: 20%;
  `}

  ${media.max('ms')`
    display: block;
    margin-bottom: ${units(6)};
    margin-top: ${units(1)};
  `}

  ${media.max('sm')`
    margin-bottom: 0;
  `}
`;

/**
 * `GalleryWrapper` styled component.
 */

const GalleryWrapper = styled.div`
  grid-area: gallery;
  grid-row: 3 / 5;
  width: 100%;
  z-index: 0;

  > div {
    height: 100%;
  }

  ${media.min('ms')`
    grid-row: 3 / 6;
  `}
`;

/**
 * `SlideItem` styled component.
 */

const SlideItem = styled.div`
  cursor: pointer;
  height: 100%;
  overflow: hidden;
  position: relative;
`;

/**
 * `CloseUpImage` styled component.
 */

const CloseUpImage = styled.div`
  align-self: center;
  grid-area: image;
  grid-row: 4 / 6;

  ${media.min('ms')`
    grid-row: 4 / 8;
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  padding-bottom: 100%;
  position: relative;

  > div {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }
`;

/**
 * `GalleryDetailsWrapper` styled component.
 */

const GalleryDetailsWrapper = styled.div`
  grid-area: galleryDetails;
  grid-row: 5;
  padding-top: ${units(2)};

  ${media.min('ms')`
    grid-row: 6;
    padding-top: ${units(2.5)};
  `}
`;

/**
 * `GalleryDetails` styled component.
 */

const GalleryDetails = styled.div`
  display: grid;
  grid-template-areas: '. imageLabel';
  grid-template-columns: ${units(2.5)} calc(100% - ${units(2.5)});

  ${media.min('ms')`
    grid-template-areas: '. imageLabel . pagination';
    grid-template-columns: ${units(4)} max-content auto max-content;
  `}
`;

/**
 * `GalleryDetailsPagination` styled component.
 */

const GalleryDetailsPagination = styled.div<{ isKiosk?: boolean }>`
  display: flex;
  flex-flow: row wrap;
  grid-area: pagination;
  grid-row: ${ifProp('isKiosk', 1, 3)};
  justify-content: flex-end;

  ${media.max('ms')`
    display: none;
  `}
`;

/**
 * `GalleryDetailsImageLabel` styled component.
 */

const GalleryDetailsImageLabel = styled(Type.Small).attrs({
  as: 'div',
  xSmall: true
})`
  color: ${color('grey900')};
  grid-area: imageLabel;
  grid-row: 1;
  justify-self: flex-start;
  min-height: 20px;
`;

/**
 * `TypologiesWrapper` styled component.
 */

const TypologiesWrapper = styled.div`
  grid-area: typologies;
  grid-row: 6 / 7;
  padding-top: ${units(5)};

  ${media.min('ms')`
    align-self: flex-end;
    grid-row: 7 / 9;
    justify-self: flex-end;
    padding-top: 0;

  `}
`;

/**
 * `Typologies` styled component.
 */

const Typologies = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-gap: ${units(5)};

  ${media.min('ms')`
    grid-gap: ${units(8)};
  `}
`;

/**
 * `SpecsWrapper` styled component.
 */

const SpecsWrapper = styled.div`
  grid-area: specs;
  grid-row: 7 / 8;
  padding-top: ${units(8)};

  ${media.min('xs')`
    padding-top: ${units(12)};
  `}

  ${media.min('ms')`
    grid-row: 9;
    padding-top: ${units(4)};
  `}

  ${media.min('md')`
    align-self: center;
    grid-row: 8 / 9;
    padding-top: 0;
  `}
`;

/**
 * `Specs` styled component.
 */

const Specs = styled.div`
  display: flex;
  flex-direction: column;

  ${media.min('xs')`
    flex-direction: row;
  `}

  ${media.min('ms')`
    align-items: flex-start;
    grid-row: 8 / 9;
    padding-top: 0;
  `}

  ${media.min('md')`
    align-items: flex-end;
    flex-flow: row nowrap;
  `}
`;

/**
 * `Spec` styled component.
 */

const Spec = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: ${units(2)};

  ${media.min('xs')`
    margin-bottom: 0;

    &:not(:last-child) {
      margin-right: ${units(2)};
    }
  `}

  ${media.min('md')`
    &:not(:last-child) {
      margin-right: ${units(4)};
    }
  `}

  ${media.min('lg')`
    &:not(:last-child) {
      margin-right: ${units(6)};
    }
  `}
`;

/**
 * `SpecAmount` styled component.
 */

const SpecAmount = styled(Label).attrs({ as: 'span', size: 'large' })`
  color: ${color('brown400')};
  grid-area: amount;
  margin-right: ${units(1.25)};
`;

/**
 * `SpecLabel` styled component.
 */

const SpecLabel = styled(Type.Small).attrs({ as: 'span' })`
  color: ${color('grey900')};
  grid-area: label;
  line-height: ${units(2)};
  text-transform: uppercase;
`;

/**
 * `StyledNavigation` styled component.
 */

const StyledNavigation = styled(Navigation)`
  grid-area: back;
  margin-bottom: ${units(5.5)};

  ${media.min('ms')`
    padding-left: ${units(3.5)};
  `}
`;

/**
 * `Header` component.
 */

const Header = (props: Props): ReactElement => {
  const {
    colorsTotal,
    finishesTotal,
    formatsTotal,
    gallery,
    imageUrl,
    isKiosk,
    isNew,
    media,
    name,
    subTitle,
    typologies
  } = props;

  const { translate } = useTranslate();
  const [isOpenLightbox, setOpenLightbox] = useState<boolean>();
  const sliderRef = useRef<any>();
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const handleSlideChange = useCallback(({ activeIndex }) => {
    setActiveSlide(activeIndex);
  }, []);

  const handleClickDot = useCallback((index: number) => {
    if (sliderRef && sliderRef.current) {
      sliderRef.current.swiper.slideTo(index);
    }
  }, []);

  const alt = media[0]?.customProperties?.title;
  const gallerySize = size(gallery);
  const specs = useMemo(() => [{
    amount: colorsTotal,
    label: translate('common:labels.color', { count: colorsTotal })
  }, {
    amount: formatsTotal,
    label: translate('common:labels.format', { count: formatsTotal })
  }, {
    amount: finishesTotal,
    label: translate('common:labels.finish', { count: finishesTotal })
  }], [colorsTotal, finishesTotal, formatsTotal, translate]);

  const handleClickSlide = useCallback(() => {
    setOpenLightbox(true);
  }, []);

  return (
    <>
      <Wrapper isKiosk={isKiosk}>
        <Container hasPadding={!isKiosk}>
          <Grid isKiosk={isKiosk}>
            {isKiosk && (
              <StyledNavigation
                href={kioskRoutes.collections}
                label={translate('common:actions.backCollections')}
              />
            )}

            {isNew && (
              <IsNew>
                <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
                  {translate('common:labels.new')}
                </ParallaxFadeInUp>
              </IsNew>
            )}

            <Title isKiosk={isKiosk}>
              <ParallaxFadeInUp parallaxOptions={{ speed: -1 }}>
                {`${name} `}

                {subTitle && (
                  <SubTitle>
                    {subTitle}
                  </SubTitle>
                )}
              </ParallaxFadeInUp>
            </Title>

            <GalleryWrapper>
              <ParallaxFadeInUp
                parallaxOptions={{
                  percentage: 0.1,
                  speed: 2
                }}
              >
                <Slider
                  onClick={handleClickSlide}
                  onSlideChange={handleSlideChange}
                  ref={sliderRef}
                >
                  {map(gallery, ({ coverImage, type, url }, index: number) => (
                    <Slide key={index}>
                      <SlideItem>
                        {type === 'image' && (
                          <Image
                            alt={name}
                            layout={'fill'}
                            objectFit={'cover'}
                            src={url}
                          />
                        )}

                        {type === 'video' && (
                          <>
                            <Image
                              alt={name}
                              layout={'fill'}
                              objectFit={'cover'}
                              src={coverImage}
                            />

                            <PlayVideoButton />
                          </>
                        )}
                      </SlideItem>
                    </Slide>
                  ))}
                </Slider>
              </ParallaxFadeInUp>
            </GalleryWrapper>

            <CloseUpImage>
              {imageUrl && (
                <ImageWrapper>
                  <FadeInUp>
                    <Image
                      alt={alt ? alt : name}
                      layout={'fill'}
                      objectFit={'cover'}
                      src={imageUrl}
                    />
                  </FadeInUp>
                </ImageWrapper>
              )}
            </CloseUpImage>

            <GalleryDetailsWrapper>
              <FadeInUp>
                <GalleryDetails>
                  {gallerySize > 1 && (
                    <GalleryDetailsPagination isKiosk={isKiosk}>
                      <GalleryNavigation
                        current={activeSlide}
                        displayPagination
                        onGoToNext={() => handleClickDot(activeSlide + 1)}
                        onGoToPrevious={() => handleClickDot(activeSlide - 1)}
                        total={gallerySize}
                      />
                    </GalleryDetailsPagination>
                  )}

                  <GalleryDetailsImageLabel>
                    {gallery[activeSlide]?.label}
                  </GalleryDetailsImageLabel>
                </GalleryDetails>
              </FadeInUp>
            </GalleryDetailsWrapper>

            <TypologiesWrapper>
              <FadeInUp>
                <Typologies>
                  {size(typologies) > 0 && typologies.map((typology: string) => (
                    <Typology
                      key={typology}
                      name={camelCase(typology)}
                    />
                  ))}
                </Typologies>
              </FadeInUp>
            </TypologiesWrapper>

            <SpecsWrapper>
              <FadeInUp>
                <Specs>
                  {map(specs, ({ amount, label }, index: number) => (
                    <Spec key={index}>
                      <SpecAmount>
                        {amount}
                      </SpecAmount>

                      <SpecLabel>
                        {label}
                      </SpecLabel>
                    </Spec>
                  ))}
                </Specs>
              </FadeInUp>
            </SpecsWrapper>
          </Grid>
        </Container>
      </Wrapper>

      {gallerySize > 0 && (
        <Lightbox
          initialSlide={activeSlide}
          isOpen={isOpenLightbox}
          items={gallery}
          onClose={() => setOpenLightbox(false)}
        />
      )}
    </>
  );
};

/**
 * Export `Header` component.
 */

export default Header;
